var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
class GlobalThrottleManager {
  constructor() {
    this.resizeCallbacks = /* @__PURE__ */ new Map();
    this.scrollCallbacks = /* @__PURE__ */ new Map();
    this.orientationCallbacks = /* @__PURE__ */ new Map();
    this.RESIZE_THROTTLE = 100;
    this.SCROLL_THROTTLE = 16;
    this.ORIENTATION_THROTTLE = 200;
    this.lastScrollY = 0;
    this.scrollDirection = "down";
    this.resizeTimer = null;
    this.scrollTimer = null;
    this.orientationTimer = null;
    this.isScrolling = false;
    this.isResizing = false;
    this.init();
  }
  init() {
    window.addEventListener("resize", this.handleResize.bind(this), { passive: true });
    window.addEventListener("scroll", this.handleScroll.bind(this), { passive: true });
    window.addEventListener("orientationchange", this.handleOrientation.bind(this), { passive: true });
    this.scheduleCleanup();
  }
  handleResize() {
    if (!this.isResizing) {
      this.isResizing = true;
      this.executeCallbacks(this.resizeCallbacks);
    }
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.isResizing = false;
      this.executeCallbacks(this.resizeCallbacks, "end");
    }, this.RESIZE_THROTTLE);
  }
  handleScroll() {
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
    this.scrollDirection = currentScrollY > this.lastScrollY ? "down" : "up";
    this.lastScrollY = currentScrollY;
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.executeCallbacks(this.scrollCallbacks, {
        phase: "start",
        scrollY: currentScrollY,
        direction: this.scrollDirection
      });
    }
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      this.isScrolling = false;
      this.executeCallbacks(this.scrollCallbacks, {
        phase: "end",
        scrollY: this.lastScrollY,
        direction: this.scrollDirection
      });
    }, this.SCROLL_THROTTLE);
  }
  handleOrientation() {
    clearTimeout(this.orientationTimer);
    this.orientationTimer = setTimeout(() => {
      this.executeCallbacks(this.orientationCallbacks);
    }, this.ORIENTATION_THROTTLE);
  }
  executeCallbacks(callbackMap, data = "active") {
    requestAnimationFrame(() => {
      this.runCallbacks(callbackMap, data);
    });
  }
  runCallbacks(callbackMap, data) {
    for (const [id, callback] of callbackMap) {
      if (typeof callback === "function") {
        try {
          callback(data);
        } catch (error) {
        }
      }
    }
  }
  // Public API for registering callbacks
  onResize(id, callback) {
    this.resizeCallbacks.set(id, callback);
    return () => this.resizeCallbacks.delete(id);
  }
  onScroll(id, callback) {
    this.scrollCallbacks.set(id, callback);
    return () => this.scrollCallbacks.delete(id);
  }
  onOrientation(id, callback) {
    this.orientationCallbacks.set(id, callback);
    return () => this.orientationCallbacks.delete(id);
  }
  // Batch remove callbacks for cleanup
  removeCallbacks(id) {
    this.resizeCallbacks.delete(id);
    this.scrollCallbacks.delete(id);
    this.orientationCallbacks.delete(id);
  }
  scheduleCleanup() {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        [this.resizeCallbacks, this.scrollCallbacks, this.orientationCallbacks].forEach((map) => {
          for (const [id, callback] of map) {
            if (!callback || !document.querySelector(`[data-throttle-id="${id}"]`)) {
              map.delete(id);
            }
          }
        });
        setTimeout(() => this.scheduleCleanup(), 6e4);
      });
    }
  }
  destroy() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("orientationchange", this.handleOrientation);
    clearTimeout(this.resizeTimer);
    clearTimeout(this.scrollTimer);
    clearTimeout(this.orientationTimer);
    this.resizeCallbacks.clear();
    this.scrollCallbacks.clear();
    this.orientationCallbacks.clear();
  }
}
const globalThrottleManager = new GlobalThrottleManager();
window.globalThrottleManager = globalThrottleManager;
class DOMHelper {
  // Cache headers for 5 seconds
  /**
   * Get all header elements with caching
   * @returns {Object} Object containing all header elements
   */
  static getAllHeaders() {
    const now = Date.now();
    if (this.headerCache && now - this.headerCache.timestamp < this.cacheTimeout) {
      return this.headerCache.headers;
    }
    const headers = {
      center: document.getElementById("header-center"),
      inline: document.getElementById("header-inline"),
      vertical: document.getElementById("header-vertical"),
      centerMobile: document.getElementById("header-center-mobile")
    };
    this.headerCache = {
      headers,
      timestamp: now
    };
    return headers;
  }
  /**
   * Get a specific header by type
   * @param {string} type - Header type: 'center', 'inline', 'vertical', 'centerMobile'
   * @returns {Element|null} Header element or null
   */
  static getHeader(type) {
    const headers = this.getAllHeaders();
    return headers[type] || null;
  }
  /**
   * Get the active header (first found in priority order)
   * @returns {Element|null} Active header element
   */
  static getActiveHeader() {
    const headers = this.getAllHeaders();
    return headers.center || headers.inline || headers.vertical || headers.centerMobile || null;
  }
  /**
   * Safe element query with optional caching
   * @param {string} selector - CSS selector
   * @param {Element|Document} context - Query context (default: document)
   * @param {boolean} cache - Whether to cache the result
   * @returns {Element|null} Found element or null
   */
  static safeQuery(selector, context = document, cache = false) {
    try {
      if (cache && this.cache.has(selector)) {
        const cached = this.cache.get(selector);
        if (cached.element && document.contains(cached.element)) {
          return cached.element;
        }
        this.cache.delete(selector);
      }
      const element = context.querySelector(selector);
      if (cache && element) {
        this.cache.set(selector, { element, timestamp: Date.now() });
        if (Math.random() < 0.01) {
          this.cleanupExpiredCache();
        }
      }
      return element;
    } catch (error) {
      return null;
    }
  }
  /**
   * Safe element query all
   * @param {string} selector - CSS selector
   * @param {Element|Document} context - Query context (default: document)
   * @returns {Element[]} Array of found elements
   */
  static safeQueryAll(selector, context = document) {
    try {
      return Array.from(context.querySelectorAll(selector));
    } catch (error) {
      return [];
    }
  }
  /**
   * Safe class addition
   * @param {Element|Element[]|null} elements - Element(s) to modify
   * @param {string|string[]} classNames - Class name(s) to add
   */
  static addClass(elements, classNames) {
    try {
      this._applyClassOperation(elements, classNames, "add");
    } catch (error) {
    }
  }
  /**
   * Safe class removal
   * @param {Element|Element[]|null} elements - Element(s) to modify
   * @param {string|string[]} classNames - Class name(s) to remove
   */
  static removeClass(elements, classNames) {
    try {
      this._applyClassOperation(elements, classNames, "remove");
    } catch (error) {
    }
  }
  /**
   * Safe class toggle
   * @param {Element|Element[]|null} elements - Element(s) to modify
   * @param {string} className - Class name to toggle
   * @param {boolean} force - Force add (true) or remove (false)
   * @returns {boolean|boolean[]} Toggle result(s)
   */
  static toggleClass(elements, className, force) {
    try {
      if (!elements) return false;
      const elemArray = Array.isArray(elements) ? elements : [elements];
      const results = elemArray.map((element) => {
        return (element == null ? void 0 : element.classList.toggle(className, force)) || false;
      });
      return Array.isArray(elements) ? results : results[0];
    } catch (error) {
      return false;
    }
  }
  /**
   * Check if element has class
   * @param {Element|null} element - Element to check
   * @param {string} className - Class name to check
   * @returns {boolean} Whether element has the class
   */
  static hasClass(element, className) {
    try {
      return (element == null ? void 0 : element.classList.contains(className)) || false;
    } catch (error) {
      return false;
    }
  }
  /**
   * Safe attribute setting
   * @param {Element|Element[]|null} elements - Element(s) to modify
   * @param {string} attribute - Attribute name
   * @param {string} value - Attribute value
   */
  static setAttribute(elements, attribute, value) {
    try {
      if (!elements) return;
      const elemArray = Array.isArray(elements) ? elements : [elements];
      elemArray.forEach((element) => {
        element == null ? void 0 : element.setAttribute(attribute, value);
      });
    } catch (error) {
    }
  }
  /**
   * Safe attribute removal
   * @param {Element|Element[]|null} elements - Element(s) to modify
   * @param {string} attribute - Attribute name to remove
   */
  static removeAttribute(elements, attribute) {
    try {
      if (!elements) return;
      const elemArray = Array.isArray(elements) ? elements : [elements];
      elemArray.forEach((element) => {
        element == null ? void 0 : element.removeAttribute(attribute);
      });
    } catch (error) {
    }
  }
  /**
   * Safe style setting
   * @param {Element|Element[]|null} elements - Element(s) to modify
   * @param {string|Object} property - Style property name or object of styles
   * @param {string} value - Style value (if property is string)
   */
  static setStyle(elements, property, value) {
    try {
      if (!elements) return;
      const elemArray = Array.isArray(elements) ? elements : [elements];
      elemArray.forEach((element) => {
        if (!element) return;
        if (typeof property === "object") {
          Object.assign(element.style, property);
        } else {
          element.style[property] = value;
        }
      });
    } catch (error) {
    }
  }
  /**
   * Wait for element to exist in DOM
   * @param {string} selector - CSS selector
   * @param {number} timeout - Timeout in milliseconds (default: 5000)
   * @param {Element|Document} context - Query context (default: document)
   * @returns {Promise<Element>} Promise that resolves with the element
   */
  static waitForElement(selector, timeout = 5e3, context = document) {
    return new Promise((resolve, reject) => {
      try {
        const element = context.querySelector(selector);
        if (element) {
          resolve(element);
          return;
        }
        const observer = new MutationObserver(() => {
          const element2 = context.querySelector(selector);
          if (element2) {
            observer.disconnect();
            resolve(element2);
          }
        });
        observer.observe(context, {
          childList: true,
          subtree: true
        });
        setTimeout(() => {
          observer.disconnect();
          reject(new Error(`Element ${selector} not found within ${timeout}ms`));
        }, timeout);
      } catch (error) {
        reject(error);
      }
    });
  }
  /**
   * Internal method to apply class operations
   * @private
   */
  static _applyClassOperation(elements, classNames, operation) {
    if (!elements) return;
    const elemArray = Array.isArray(elements) ? elements : [elements];
    const classArray = Array.isArray(classNames) ? classNames : [classNames];
    elemArray.forEach((element) => {
      if (!element) return;
      classArray.forEach((className) => {
        element.classList[operation](className);
      });
    });
  }
  /**
   * Clear element cache (useful for cleanup)
   */
  static clearCache() {
    this.cache.clear();
    this.headerCache = null;
  }
  /**
   * Clean up expired cache entries
   */
  static cleanupExpiredCache() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.cache.delete(key);
      }
    }
  }
  /**
   * Check if an element exists and is visible
   * @param {Element|null} element - Element to check
   * @returns {boolean} Whether element exists and is visible
   */
  static isVisible(element) {
    try {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
    } catch (error) {
      return false;
    }
  }
  /**
   * Get element's offset position relative to document
   * @param {Element|null} element - Element to get position for
   * @returns {Object|null} Object with top, left, width, height properties
   */
  static getElementPosition(element) {
    try {
      if (!element) return null;
      const rect = element.getBoundingClientRect();
      return {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      };
    } catch (error) {
      return null;
    }
  }
  /**
   * Dispatch a custom event on an element or document
   * @param {string} eventName - Name of the custom event
   * @param {Object} detail - Event detail data
   * @param {Element|Document} target - Target element (default: document)
   * @returns {boolean} Whether the event was successfully dispatched
   */
  static dispatchEvent(eventName, detail = {}, target = document) {
    try {
      if (!eventName) return false;
      const event = new CustomEvent(eventName, { detail });
      return target.dispatchEvent(event);
    } catch (error) {
      return false;
    }
  }
  /**
   * Get attribute value with fallback
   * @param {Element|null} element - Element to get attribute from
   * @param {string} attribute - Attribute name
   * @param {string} fallback - Fallback value if attribute doesn't exist
   * @returns {string} Attribute value or fallback
   */
  static getAttribute(element, attribute, fallback = "") {
    try {
      return (element == null ? void 0 : element.getAttribute(attribute)) || fallback;
    } catch (error) {
      return fallback;
    }
  }
  /**
   * Check if element has attribute
   * @param {Element|null} element - Element to check
   * @param {string} attribute - Attribute name
   * @returns {boolean} Whether element has the attribute
   */
  static hasAttribute(element, attribute) {
    try {
      return (element == null ? void 0 : element.hasAttribute(attribute)) || false;
    } catch (error) {
      return false;
    }
  }
  /**
   * Create element with properties and content
   * @param {string} tagName - HTML tag name
   * @param {Object} options - Element configuration
   * @param {string} options.className - CSS class names
   * @param {string} options.id - Element ID
   * @param {string} options.innerHTML - Element inner HTML
   * @param {string} options.textContent - Element text content
   * @param {Object} options.attributes - Object of attributes to set
   * @param {Object} options.styles - Object of styles to set
   * @returns {Element} Created element
   */
  static createElement(tagName, options = {}) {
    try {
      const element = document.createElement(tagName);
      if (options.className) {
        element.className = options.className;
      }
      if (options.id) {
        element.id = options.id;
      }
      if (options.innerHTML) {
        element.innerHTML = options.innerHTML;
      } else if (options.textContent) {
        element.textContent = options.textContent;
      }
      if (options.attributes) {
        Object.entries(options.attributes).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
      }
      if (options.styles) {
        Object.assign(element.style, options.styles);
      }
      return element;
    } catch (error) {
      return null;
    }
  }
  /**
   * Create and append element to parent
   * @param {Element} parent - Parent element
   * @param {string} tagName - HTML tag name
   * @param {Object} options - Element configuration (same as createElement)
   * @returns {Element} Created element
   */
  static createAndAppend(parent, tagName, options = {}) {
    try {
      if (!parent) return null;
      const element = this.createElement(tagName, options);
      if (element) {
        parent.appendChild(element);
      }
      return element;
    } catch (error) {
      return null;
    }
  }
  /**
   * Remove element safely
   * @param {Element|null} element - Element to remove
   * @returns {boolean} Whether element was removed
   */
  static removeElement(element) {
    try {
      if (!element || !element.parentNode) return false;
      element.parentNode.removeChild(element);
      return true;
    } catch (error) {
      return false;
    }
  }
  /**
   * Empty element content safely
   * @param {Element|null} element - Element to empty
   */
  static emptyElement(element) {
    try {
      if (!element) return;
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }
    } catch (error) {
    }
  }
  /**
   * Replace element content safely
   * @param {Element|null} element - Element to update
   * @param {string|Element} content - New content (HTML string or element)
   */
  static replaceContent(element, content) {
    try {
      if (!element) return;
      if (typeof content === "string") {
        element.innerHTML = content;
      } else if (content instanceof Element) {
        this.emptyElement(element);
        element.appendChild(content);
      }
    } catch (error) {
    }
  }
  /**
   * Insert element after another element
   * @param {Element} newElement - Element to insert
   * @param {Element} referenceElement - Reference element
   * @returns {boolean} Whether insertion was successful
   */
  static insertAfter(newElement, referenceElement) {
    try {
      if (!newElement || !referenceElement || !referenceElement.parentNode) return false;
      referenceElement.parentNode.insertBefore(newElement, referenceElement.nextSibling);
      return true;
    } catch (error) {
      return false;
    }
  }
  /**
   * Get computed style property safely
   * @param {Element|null} element - Element to check
   * @param {string} property - CSS property name
   * @returns {string} Property value or empty string
   */
  static getComputedStyle(element, property) {
    try {
      if (!element) return "";
      const styles = window.getComputedStyle(element);
      return styles.getPropertyValue(property) || "";
    } catch (error) {
      return "";
    }
  }
}
__publicField(DOMHelper, "cache", /* @__PURE__ */ new Map());
__publicField(DOMHelper, "headerCache", null);
__publicField(DOMHelper, "cacheTimeout", 5e3);
if (typeof window !== "undefined") {
  window.DOMHelper = DOMHelper;
}
/* ===================================================================================== @preserve =
 ___  _   _    _
/   || | | |  | |
\__  | | | |  | |  __
/    |/  |/_) |/  /  \_/\/
\___/|__/| \_/|__/\__/  /\_/
              |\
              |/
Ajaxinate
version v2.0.11
https://github.com/Elkfox/Ajaxinate
Copyright (c) 2017 Elkfox Co Pty Ltd
https://elkfox.com
MIT License
================================================================================================= */
var Ajaxinate = function ajaxinateConstructor(config) {
  var settings = config || {};
  var defaultSettings = {
    pagination: "#AjaxinatePagination",
    method: "scroll",
    container: "#AjaxinateLoop",
    offset: 0,
    loadingText: '<div class="ajax-loading-animation"><div></div><div></div><div></div></div>',
    callback: null
  };
  this.settings = Object.assign(defaultSettings, settings);
  this.addScrollListeners = this.addScrollListeners.bind(this);
  this.addClickListener = this.addClickListener.bind(this);
  this.checkIfPaginationInView = this.checkIfPaginationInView.bind(this);
  this.stopMultipleClicks = this.stopMultipleClicks.bind(this);
  this.destroy = this.destroy.bind(this);
  this.containerElement = document.querySelector(this.settings.container);
  this.paginationElement = document.querySelector(this.settings.pagination);
  this.initialize();
};
Ajaxinate.prototype.initialize = function initializeTheCorrectFunctionsBasedOnTheMethod() {
  if (this.containerElement) {
    var initializers = {
      click: this.addClickListener,
      scroll: this.addScrollListeners
    };
    initializers[this.settings.method]();
  }
};
Ajaxinate.prototype.addScrollListeners = function addEventListenersForScrolling() {
  if (this.paginationElement) {
    document.addEventListener("scroll", this.checkIfPaginationInView);
    window.addEventListener("resize", this.checkIfPaginationInView);
    window.addEventListener("orientationchange", this.checkIfPaginationInView);
  }
};
Ajaxinate.prototype.addClickListener = function addEventListenerForClicking() {
  if (this.paginationElement) {
    this.nextPageLinkElement = this.paginationElement.querySelector("a");
    this.clickActive = true;
    if (this.nextPageLinkElement !== null) {
      this.nextPageLinkElement.addEventListener("click", this.stopMultipleClicks);
    }
  }
};
Ajaxinate.prototype.stopMultipleClicks = function handleClickEvent(event) {
  event.preventDefault();
  if (this.clickActive) {
    this.nextPageLinkElement.innerHTML = this.settings.loadingText;
    this.nextPageUrl = this.nextPageLinkElement.href;
    this.clickActive = false;
    this.loadMore();
  }
};
Ajaxinate.prototype.checkIfPaginationInView = function handleScrollEvent() {
  var top = this.paginationElement.getBoundingClientRect().top - this.settings.offset;
  var bottom = this.paginationElement.getBoundingClientRect().bottom + this.settings.offset;
  if (top <= window.innerHeight && bottom >= 0) {
    this.nextPageLinkElement = this.paginationElement.querySelector("a");
    this.removeScrollListener();
    if (this.nextPageLinkElement) {
      this.nextPageLinkElement.innerHTML = this.settings.loadingText;
      this.nextPageUrl = this.nextPageLinkElement.href;
      this.loadMore();
    }
  }
};
Ajaxinate.prototype.loadMore = function getTheHtmlOfTheNextPageWithAnAjaxRequest() {
  this.request = new XMLHttpRequest();
  this.request.onreadystatechange = function success() {
    if (this.request.readyState === 4 && this.request.status === 200) {
      var newContainer = this.request.responseXML.querySelectorAll(this.settings.container)[0];
      var newPagination = this.request.responseXML.querySelectorAll(this.settings.pagination)[0];
      var newProducts = newContainer.children;
      if (document.body.getAttribute("data-page-animation") === "section_fade") {
        for (var i = 0; i < newProducts.length; i++) {
          newProducts[i].classList.add("ajax-fade-in");
        }
      }
      this.containerElement.insertAdjacentHTML("beforeend", newContainer.innerHTML);
      this.paginationElement.innerHTML = newPagination.innerHTML;
      if (this.settings.callback && typeof this.settings.callback === "function") {
        this.settings.callback(this.request.responseXML);
      }
      this.initialize();
    }
  }.bind(this);
  this.request.open("GET", this.nextPageUrl);
  this.request.responseType = "document";
  this.request.send();
};
Ajaxinate.prototype.removeClickListener = function removeClickEventListener() {
  this.nextPageLinkElement.addEventListener("click", this.stopMultipleClicks);
};
Ajaxinate.prototype.removeScrollListener = function removeScrollEventListener() {
  document.removeEventListener("scroll", this.checkIfPaginationInView);
  window.removeEventListener("resize", this.checkIfPaginationInView);
  window.removeEventListener("orientationchange", this.checkIfPaginationInView);
};
Ajaxinate.prototype.destroy = function removeEventListenersAndReturnThis() {
  var destroyers = {
    click: this.removeClickListener,
    scroll: this.removeScrollListener
  };
  destroyers[this.settings.method]();
  return this;
};
if (typeof window !== "undefined") {
  window.Ajaxinate = Ajaxinate;
}
function initGlobalAjaxinate(containerId = null) {
  const blogContainer = document.querySelector("#BlogGridContainer");
  if (blogContainer) {
    return new Ajaxinate({
      container: "#BlogGridContainer",
      pagination: "#AjaxinatePagination",
      method: "scroll",
      offset: 500
    });
  }
  const productGrid = containerId ? document.querySelector(containerId.startsWith("#") ? containerId : `#${containerId}`) : document.querySelector("#product-grid") || document.querySelector('[id^="collection-grid-"]') || document.querySelector('[id^="search-results-"]');
  if (!productGrid) {
    return;
  }
  const paginationElement = document.querySelector("#AjaxinatePagination");
  if (!paginationElement) {
    return;
  }
  let isLoading = false;
  let currentPage = 1;
  function shouldLoadMore() {
    if (isLoading) return false;
    if (!paginationElement.querySelector("a[href]")) return false;
    const rect = productGrid.getBoundingClientRect();
    const buffer = 500;
    return rect.bottom - buffer <= window.innerHeight;
  }
  function loadNextPage() {
    return __async(this, null, function* () {
      if (isLoading) return;
      const nextLink = paginationElement.querySelector("a[href]");
      if (!nextLink) return;
      isLoading = true;
      const loadingAnimation = document.createElement("div");
      loadingAnimation.className = "ajax-loading-animation";
      loadingAnimation.innerHTML = "<div></div><div></div><div></div>";
      nextLink.innerHTML = "";
      nextLink.appendChild(loadingAnimation);
      const nextUrl = nextLink.href;
      try {
        const response = yield window.ShopifyAPIHelper.safeFetch(nextUrl);
        const html = yield response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newGrid = doc.querySelector(productGrid.id ? `#${productGrid.id}` : '[id^="collection-grid-"]');
        const newPagination = doc.querySelector("#AjaxinatePagination");
        if (newGrid) {
          const newProducts = Array.from(newGrid.children).filter(
            (child) => child.querySelector("[data-product-id]")
          );
          const existingProducts = Array.from(productGrid.children).filter(
            (child) => child.querySelector("[data-product-id]")
          );
          const existingIds = new Set(
            existingProducts.map(
              (el) => el.querySelector("[data-product-id]").dataset.productId
            )
          );
          if (window.SwatchStore) {
            doc.querySelectorAll("script.swatch-data").forEach((script) => {
              try {
                const data = JSON.parse(script.textContent);
                window.SwatchStore.add(data);
              } catch (e) {
              }
            });
          }
          const newlyAddedProducts = [];
          newProducts.forEach((product) => {
            var _a;
            const productId = (_a = product.querySelector("[data-product-id]")) == null ? void 0 : _a.dataset.productId;
            if (productId && !existingIds.has(productId)) {
              if (document.body.getAttribute("data-page-animation") === "section_fade") {
                product.classList.add("ajax-fade-in");
              }
              productGrid.appendChild(product);
              existingIds.add(productId);
              newlyAddedProducts.push(product);
            }
          });
          if (typeof window.initializeProductCards === "function") {
            window.initializeProductCards();
          }
          if (typeof window.initializeImageLoading === "function") {
            yield new Promise((resolve) => requestAnimationFrame(resolve));
            newlyAddedProducts.forEach((product) => {
              window.initializeImageLoading(product);
            });
            yield Promise.all(
              newlyAddedProducts.flatMap(
                (product) => Array.from(product.querySelectorAll(".responsive-image-container img"))
              ).map(
                (img) => new Promise((resolve) => {
                  if (img.complete) {
                    resolve();
                  } else {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                  }
                })
              )
            );
          }
          if (newPagination) {
            paginationElement.innerHTML = newPagination.innerHTML;
          } else {
            paginationElement.innerHTML = "";
          }
          currentPage++;
          document.dispatchEvent(new CustomEvent("theme:products:loaded"));
          document.dispatchEvent(new CustomEvent("theme:ajaxinate:updated"));
        }
      } catch (error) {
      } finally {
        isLoading = false;
      }
    });
  }
  let scrollTimeout;
  function handleScroll() {
    if (scrollTimeout) return;
    scrollTimeout = setTimeout(() => {
      if (shouldLoadMore()) {
        loadNextPage();
      }
      scrollTimeout = null;
    }, 100);
  }
  window.addEventListener("scroll", handleScroll, { passive: true });
  setTimeout(() => {
    if (shouldLoadMore()) {
      loadNextPage();
    }
  }, 100);
  return function destroy() {
    window.removeEventListener("scroll", handleScroll);
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
  };
}
document.addEventListener("DOMContentLoaded", () => {
  initGlobalAjaxinate();
});
window.initGlobalAjaxinate = initGlobalAjaxinate;
document.addEventListener("DOMContentLoaded", () => {
  const body = document.body;
  const pageAnimation = body.getAttribute("data-page-animation");
  const isPageFadeEnabled = pageAnimation === "page_fade";
  if (isPageFadeEnabled) {
    body.classList.add("page-fade-active");
    setTimeout(() => {
      body.classList.add("page-loaded");
      const mainElement = document.getElementById("main");
      if (mainElement) {
        const transitionDuration = getComputedStyle(mainElement).transitionDuration;
        const durationMs = parseFloat(transitionDuration) * (transitionDuration.indexOf("ms") > -1 ? 1 : 1e3);
        setTimeout(() => {
          body.classList.add("page-fade-complete");
          const fadeCompleteEvent = new CustomEvent("theme:page-fade:complete", {
            bubbles: true,
            detail: { timestamp: Date.now() }
          });
          document.dispatchEvent(fadeCompleteEvent);
          if (typeof AOS !== "undefined") {
            AOS.refresh();
          }
        }, durationMs + 50);
      }
    }, 50);
  } else {
    body.classList.add("page-loaded");
    body.classList.add("page-fade-complete");
    const fadeCompleteEvent = new CustomEvent("theme:page-fade:complete", {
      bubbles: true,
      detail: { enabled: false, timestamp: Date.now() }
    });
    document.dispatchEvent(fadeCompleteEvent);
  }
  document.addEventListener("beforeunload", () => {
    if (isPageFadeEnabled) {
      body.classList.remove("page-loaded");
      body.classList.remove("page-fade-complete");
    }
  });
});
document.addEventListener("DOMContentLoaded", () => {
  initializeImageLoading();
  setupImageObserver();
});
function setupImageObserver() {
  if (window.imageLoadingObserver) return;
  const observer = new MutationObserver((mutations) => {
    let shouldInitialize = false;
    mutations.forEach((mutation) => {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        for (let i = 0; i < mutation.addedNodes.length; i++) {
          const node = mutation.addedNodes[i];
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains("image-loading") || node.querySelectorAll && node.querySelectorAll(".image-loading:not(.image-loaded)").length > 0) {
              shouldInitialize = true;
              break;
            }
          }
        }
      }
    });
    if (shouldInitialize) {
      initializeImageLoading();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.imageLoadingObserver = observer;
}
window.initializeImageLoading = function(container = document) {
  const containers = (() => {
    if (!container) return [document];
    if (container instanceof Document) return [document];
    if (typeof container === "string") {
      const element = document.querySelector(container);
      return element ? [element] : [];
    }
    if (container instanceof Element) return [container];
    if (container instanceof NodeList || container instanceof HTMLCollection || Array.isArray(container)) {
      return Array.from(container).filter((el) => el && typeof el.querySelectorAll === "function");
    }
    if (typeof container.querySelectorAll === "function") return [container];
    return [document];
  })();
  const images = containers.flatMap(
    (root) => Array.from(root.querySelectorAll(".image-loading:not(.image-loaded)"))
  );
  if (images.length === 0) {
    return Promise.resolve();
  }
  return Promise.all(Array.from(new Set(images)).map(processImage));
};
function processImage(img) {
  return new Promise((resolve) => {
    const isHoverImage = img.closest(".hover-image-container") !== null;
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add("image-loaded");
      handlePlaceholder(img, isHoverImage);
      resolve();
      return;
    }
    img.onload = function() {
      img.classList.add("image-loaded");
      handlePlaceholder(img, isHoverImage);
      resolve();
    };
    img.onerror = function() {
      img.classList.add("image-error");
      img.classList.add("image-loaded");
      handlePlaceholder(img, isHoverImage);
      resolve();
    };
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add("image-loaded");
      handlePlaceholder(img, isHoverImage);
      resolve();
    }
  });
}
function handlePlaceholder(img, isHoverImage) {
  if (isHoverImage) return;
  const container = img.closest(".responsive-image-container");
  if (!container) return;
  const placeholder = container.querySelector(".image-placeholder");
  if (!placeholder) return;
  placeholder.classList.add("image-placeholder-fading");
  placeholder.addEventListener("transitionend", function(e) {
    if (e.propertyName === "opacity" && placeholder.parentNode) {
      placeholder.remove();
    }
  }, { once: true });
}
const contentUpdateEvents = [
  "load",
  "shopify:section:load",
  "theme:ajaxinate:updated",
  "theme:collection:updated",
  "theme:cart:open",
  "page:loaded",
  "criticalReady"
];
contentUpdateEvents.forEach((eventName) => {
  document.addEventListener(eventName, () => {
    initializeImageLoading();
    requestAnimationFrame(initializeImageLoading);
  });
});
if (window.Shopify && window.Shopify.designMode !== true) {
  setTimeout(() => {
    initializeImageLoading();
  }, 1e3);
}
window.fixStuckImages = function() {
  const stuckImages = document.querySelectorAll(".image-loading:not(.image-loaded)");
  stuckImages.forEach((img) => {
    img.classList.add("image-loaded");
    const container = img.closest(".responsive-image-container");
    if (container) {
      const placeholder = container.querySelector(".image-placeholder");
      if (placeholder && placeholder.parentNode) {
        placeholder.remove();
      }
    }
  });
  return `Fixed ${stuckImages.length} stuck images`;
};
window.checkImageLoadingStatus = function() {
  const notLoaded = document.querySelectorAll(".image-loading:not(.image-loaded)");
  const loaded = document.querySelectorAll(".image-loading.image-loaded");
  const errors = document.querySelectorAll(".image-loading.image-error");
  return {
    total: notLoaded.length + loaded.length,
    loaded: loaded.length,
    loading: notLoaded.length,
    errors: errors.length,
    notLoadedImages: Array.from(notLoaded).map((img) => ({
      src: img.src,
      complete: img.complete,
      naturalWidth: img.naturalWidth
    }))
  };
};
class ModuleManager {
  constructor() {
    this.modules = /* @__PURE__ */ new Map();
    this.isReady = false;
    this.readyQueue = [];
  }
  register(name, initFunction, dependencies = ["DOMHelper"]) {
    this.modules.set(name, {
      init: initFunction,
      dependencies,
      initialized: false
    });
    if (this.isReady) {
      this.initializeModule(name);
    }
  }
  checkDependencies(dependencies) {
    return dependencies.every((dep) => typeof window[dep] !== "undefined");
  }
  initializeModule(name) {
    const module = this.modules.get(name);
    if (!module || module.initialized) return;
    if (this.checkDependencies(module.dependencies)) {
      try {
        module.init();
        module.initialized = true;
      } catch (error) {
      }
    }
  }
  initializeAll() {
    this.isReady = true;
    this.modules.forEach((_, name) => {
      this.initializeModule(name);
    });
    this.readyQueue.forEach((callback) => callback());
    this.readyQueue = [];
  }
  onReady(callback) {
    if (this.isReady) {
      callback();
    } else {
      this.readyQueue.push(callback);
    }
  }
}
window.ModuleManager = new ModuleManager();
window.Ajaxinate = Ajaxinate;
document.addEventListener("DOMContentLoaded", () => {
  setTimeout(() => {
    window.ModuleManager.initializeAll();
  }, 100);
  document.dispatchEvent(new Event("criticalReady"));
});
